/**
 * The task picker, searchable.
 *
 * A native select on a phone is a wheel, and a wheel of sixty tasks is a lot of
 * flicking to find "Board deck". Tapping this opens a box you can type into:
 * a couple of letters narrows the list (title or section, every word must
 * match), and the list is still there to scroll if you'd rather browse.
 */

import { useMemo, useState } from "react";

import { filterTaskGroups, type TaskGroup } from "@/lib/taskOptions";

import { field } from "./atoms";

export function TaskPicker({
  value, onChange, groups, disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  groups: TaskGroup[];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = useMemo(() => {
    for (const g of groups) {
      const t = g.tasks.find((x) => x.id === value);
      if (t) return t;
    }
    return undefined;
  }, [groups, value]);

  const filtered = useMemo(() => filterTaskGroups(groups, query), [groups, query]);

  function pick(id: string) {
    onChange(id);
    setOpen(false);
    setQuery("");
  }

  if (!open) {
    return (
      <div style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={disabled}
          aria-label="Task"
          style={{
            ...field, display: "flex", alignItems: "center", gap: 8, textAlign: "left",
            cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.55 : 1,
            paddingRight: selected ? 44 : 12,
          }}
        >
          <span style={{
            flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            color: selected ? "var(--ink)" : "var(--muted)",
          }}>{selected?.title ?? "— No specific task"}</span>
          {!selected && <span aria-hidden style={{ flex: "none", color: "var(--muted2)", fontSize: 13 }}>▾</span>}
        </button>
        {selected && !disabled && (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label="Clear task"
            style={{
              position: "absolute", right: 2, top: 0, bottom: 0, width: 44,
              border: "none", background: "none", color: "var(--muted2)", fontSize: 18, cursor: "pointer",
            }}
          >×</button>
        )}
      </div>
    );
  }

  const row = {
    display: "block", width: "100%", minHeight: 44, padding: "10px 12px", textAlign: "left" as const,
    border: "none", borderTop: "1px solid var(--tint2)", background: "none",
    fontSize: 15, color: "var(--ink)", cursor: "pointer",
  };

  return (
    <div style={{ border: "1px solid var(--accentLine)", borderRadius: 10, background: "var(--surface)", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { setOpen(false); setQuery(""); }
            // Enter takes the only match, or the first one.
            if (e.key === "Enter" && query.trim()) {
              const first = filtered[0]?.tasks[0];
              if (first) pick(first.id);
            }
          }}
          placeholder="Type to find a task…"
          aria-label="Find a task"
          enterKeyHint="done"
          style={{ ...field, flex: 1, minWidth: 0, border: "none", background: "transparent", outline: "none" }}
        />
        <button
          type="button"
          onClick={() => { setOpen(false); setQuery(""); }}
          style={{ flex: "none", minHeight: 48, padding: "0 12px", border: "none", background: "none", color: "var(--muted)", fontSize: 14, cursor: "pointer" }}
        >Cancel</button>
      </div>

      <div style={{ maxHeight: 280, overflowY: "auto", overscrollBehavior: "contain" }}>
        {!query.trim() && (
          <button type="button" onClick={() => pick("")} style={{ ...row, color: "var(--muted)" }}>
            — No specific task
          </button>
        )}
        {filtered.map((g) => (
          <div key={g.phaseId}>
            {groups.length > 1 && (
              <div style={{
                padding: "8px 12px 4px", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
                color: "var(--muted2)", borderTop: "1px solid var(--tint2)", background: "var(--tint)",
              }}>{g.phaseName}</div>
            )}
            {g.tasks.map((t) => {
              const on = t.id === value;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => pick(t.id)}
                  style={{ ...row, color: on ? "var(--accent)" : "var(--ink)", fontWeight: on ? 600 : 400 }}
                >{t.title}</button>
              );
            })}
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: "12px", fontSize: 14, color: "var(--muted)", borderTop: "1px solid var(--tint2)" }}>
            No task matches “{query.trim()}”
          </div>
        )}
      </div>
    </div>
  );
}
